import React from 'react';
import { Moon, Star, Globe, Target, Zap, Shield, Brain, BookOpen, Download, Sparkles, ChevronRight, Lock, Eye, Pyramid, Infinity, Waves, Orbit } from 'lucide-react';

function VedikshhaMysticalSection() {
    const mysticalFeatures = [
        { 
            icon: <Pyramid size={24} />, 
            title: "Vedic & Tantric Wisdom", 
            description: "Sacred teachings preserved for centuries",
            gradient: "from-amber-600/20 to-orange-600/20",
            border: "border-amber-700/30"
        },
        { 
            icon: <Orbit size={24} />, 
            title: "Cosmic Astrology", 
            description: "Planetary energies & karmic patterns",
            gradient: "from-purple-600/20 to-indigo-600/20",
            border: "border-purple-700/30"
        },
        { 
            icon: <Infinity size={24} />, 
            title: "Numerology Science", 
            description: "Decode life paths & destiny vibrations",
            gradient: "from-blue-600/20 to-cyan-600/20",
            border: "border-blue-700/30"
        },
        { 
            icon: <Waves size={24} />, 
            title: "Energy Practices", 
            description: "Meditation, mantras & inner balance",
            gradient: "from-emerald-600/20 to-teal-600/20",
            border: "border-emerald-700/30"
        },
        { 
            icon: <Shield size={24} />, 
            title: "Spiritual Protection", 
            description: "Ancient protection techniques",
            gradient: "from-violet-600/20 to-purple-600/20",
            border: "border-violet-700/30"
        },
        { 
            icon: <Brain size={24} />, 
            title: "Consciousness Expansion", 
            description: "Awaken higher states of awareness",
            gradient: "from-rose-600/20 to-pink-600/20",
            border: "border-rose-700/30"
        }
    ];

    const mysticalPrinciples = [
        { symbol: "☿", title: "Hermetic Principles", desc: "As above, so below" },
        { symbol: "♆", title: "Neptunian Mysteries", desc: "Unseen realms & intuition" },
        { symbol: "☉", title: "Solar Consciousness", desc: "Inner light & enlightenment" },
        { symbol: "☽", title: "Lunar Wisdom", desc: "Cycles & subconscious" }
    ];

    return (
        <div className="relative overflow-hidden bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-20">
            {/* Animated Background */}
            <div className="absolute inset-0">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent animate-pulse"></div>
                <div className="absolute top-1/4 -left-20 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-600/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
                
                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `linear-gradient(to right, #9f7aea 1px, transparent 1px),
                                        linear-gradient(to bottom, #9f7aea 1px, transparent 1px)`,
                        backgroundSize: '50px 50px'
                    }}></div>
                </div>
            </div>

            <div className="relative max-w-6xl mx-auto px-4">
                {/* Mystical Header */}
                <div className="text-center mb-20">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-md rounded-2xl border border-purple-700/40 mb-8 shadow-2xl">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping"></div>
                            <span className="text-purple-300 font-medium tracking-wider">A SACRED GATEWAY • UNIT OF KFE</span>
                        </div>
                    </div>
                    
                    <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
                        <span className="relative inline-block">
                            <span className="relative z-10 bg-gradient-to-r from-amber-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                                VEDIKSHHA
                            </span>
                            <span className="absolute -bottom-2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent"></span>
                        </span>
                    </h1>
                    
                    <div className="mb-8">
                        <p className="text-3xl md:text-4xl text-gray-300 mb-4 font-light">
                            Unlock the{' '}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-purple-300 font-semibold">
                                Ancient Science
                            </span>{' '}
                            of the Occult
                        </p>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                            Discover Hidden Wisdom. Transform Your Inner World.
                        </p>
                    </div>
                </div>

                {/* Mystical Cards Grid */}
                <div className="mb-16">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {mysticalFeatures.map((feature, index) => (
                            <div 
                                key={index}
                                className={`group relative overflow-hidden rounded-2xl p-6 backdrop-blur-sm border ${feature.border} ${feature.gradient} transition-all duration-500 hover:scale-[1.02]`}
                            >
                                {/* Animated Border */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 animate-shimmer"></div>
                                
                                <div className="relative z-10">
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl bg-gradient-to-br ${feature.gradient} border ${feature.border}`}>
                                            <div className="text-white">{feature.icon}</div>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white mb-2 text-lg">{feature.title}</h4>
                                            <p className="text-gray-400 text-sm">{feature.description}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Content Sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                    {/* Left - Mystical Principles */}
                    <div className="space-y-8">
                        <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-2xl border border-amber-700/30 p-8">
                            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <Eye className="text-amber-400" />
                                Ancient Mystical Principles
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                {mysticalPrinciples.map((principle, index) => (
                                    <div key={index} className="text-center p-4 rounded-xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50">
                                        <div className="text-3xl mb-2">{principle.symbol}</div>
                                        <h4 className="font-semibold text-white mb-1">{principle.title}</h4>
                                        <p className="text-sm text-gray-400">{principle.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right - Call to Action */}
                    <div className="space-y-8">
                        <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm rounded-2xl border border-purple-700/30 p-8">
                            <h3 className="text-2xl font-bold text-white mb-6">
                                Begin Your Journey
                            </h3>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Vedikshha is a sacred gateway to ancient occult sciences, 
                                designed for seekers who wish to understand the unseen forces 
                                that shape life, destiny, and consciousness.
                            </p>
                            
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                                    <span className="text-gray-300">Rooted in timeless Vedic wisdom</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse delay-300"></div>
                                    <span className="text-gray-300">Modern mobile app experience</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse delay-500"></div>
                                    <span className="text-gray-300">From beginner to advanced mastery</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main CTA */}
                <div className="text-center relative">
                    <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
                        <div className="w-20 h-20 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 rounded-full flex items-center justify-center">
                            <div className="w-10 h-10 border-2 border-purple-400 rounded-full animate-spin-slow"></div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-3xl border border-purple-700/30 p-10 mb-10">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Where{' '}
                            <span className="bg-gradient-to-r from-amber-400 to-purple-400 bg-clip-text text-transparent">
                                Ancient Wisdom
                            </span>{' '}
                            Meets{' '}
                            <span className="bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
                                Modern Learning
                            </span>
                        </h2>
                        
                        <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
                            Step into the ancient world of occult science and rediscover knowledge 
                            that empowers, enlightens, and transforms.
                        </p>

                        <button className="group relative px-10 py-5 bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white font-bold rounded-2xl hover:shadow-2xl hover:shadow-purple-500/30 transition-all duration-300 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative flex items-center justify-center gap-3">
                                <Download size={22} />
                                <span className="text-lg">Download Vedikshha App</span>
                                <Sparkles size={18} className="group-hover:animate-bounce" />
                            </div>
                        </button>
                    </div>

                    <p className="text-gray-500 text-sm italic">
                        "Vedikshha is not about superstition—it is about understanding higher laws of existence."
                    </p>
                </div>
            </div>

            {/* Add this to your global CSS */}
            <style jsx>{`
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                .animate-shimmer {
                    animation: shimmer 2s infinite;
                }
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-spin-slow {
                    animation: spin-slow 20s linear infinite;
                }
            `}</style>
        </div>
    );
}

export default VedikshhaMysticalSection;